import {AfterViewInit, Component, EventEmitter, OnDestroy, Output, Renderer2} from '@angular/core';
import {ShellService} from '@edifecs/fp-ui-shell';
import {DataEnv, DataEnvServer, EnvService} from '@app/core/services/EnvService';
import {ufn} from '@edifecs/fp-ui-lib';
import {EdfSettingsService} from '@edfx/edifecs-ui-lib';
import {Subscription} from 'rxjs';

/**
 * Component to show environment selection.
 */
@Component({
    selector: 'app-env-list',
    templateUrl: './EnvListComponent.html',
    styleUrls: ['./EnvListComponent.css'],
    standalone: false
})
export class EnvListComponent implements AfterViewInit, OnDestroy {

    /**
     * Emitted when this Ui should be closed.
     */
    @Output()
    readonly closeSelect = new EventEmitter<void>();

    /**
     * Frontend settings
     */
    showFpEnvInfo?: boolean;

    /**
     * Frontend settings subscription
     */
    edfSettingsSubscription: Subscription;

    /**
     * Constructor
     * @param envSvc Environment service
     * @param renderer
     * @param edfSettings
     */
    constructor(private readonly envSvc: EnvService,
                private renderer: Renderer2, private edfSettings: EdfSettingsService) {
        this.edfSettingsSubscription = this.edfSettings.settings.subscribe((settings: any) => {
            this.showFpEnvInfo = settings?.showFpEnvInfo;
        });
    }

    ngAfterViewInit(): void {
        // updated styling for server switcher window to match styling with environment switcher
        const element = document.getElementsByTagName('fp-modal-window');
        const offSetTop = document.getElementById('btn-server-switcher')?.offsetTop;
        if (element && offSetTop !== undefined) {
            this.renderer.setStyle(element[0], 'padding-top', `${offSetTop}px`);
            this.renderer.setStyle(element[0], 'background-color', 'unset');
        }
    }

    ngOnDestroy(): void {
        if (this.edfSettingsSubscription) {
            this.edfSettingsSubscription.unsubscribe();
        }
    }

    /**
     * @internal
     * Available environments.
     */
    get envs(): DataEnv[] {
        return this.envSvc.allEnvironments;
    }

    /**
     * @internal
     * Check if given environment is the currently selected one
     * @param env Environment
     */
    isCurrentEnv(env: DataEnv): boolean {
        return this.envSvc.current?.env === env;
    }

    /**
     * @internal
     * Check if given server is the currently selected one
     * @param srv Server
     */
    isCurrentSrv(srv: DataEnvServer): boolean {
        return this.envSvc.current?.srv === srv;
    }

    /**
     * @internal
     * Get display title for environment
     * @param env Environment
     */
    getEnvTitle(env: DataEnv): string {
        return (env.name ?? '') + (ufn.isText(env.shortName) ? ` (${env.shortName.toUpperCase()})` : '');
    }

    /**
     * @internal
     * Get display title for server
     * @param srv Environment Server
     */
    getSrvTitle(srv: DataEnvServer): string {
        return (srv.name ?? '') + (ufn.isText(srv.shortName) ? ` (${srv.shortName.toUpperCase()})` : '');
    }

    /**
     * Get hover title for server
     * @param srv Environment Server
     */
    getSrvHoverTitle(srv: DataEnvServer): string {
        return this.getSrvTitle(srv) + `\nEnvironment ID: ${srv.envId}\nServer ID: ${srv.serverId}`;
    }

    /**
     * @internal
     * Handle server selection
     * @param srv Selected server
     */
    onSelect(srv: DataEnvServer): void {
        if (ufn.isText(srv.envId) && ufn.isText(srv.serverId)) {
            this.envSvc.setSelected(srv.envId, srv.serverId);
            window.location.replace('/');
        }
        this.closeSelect.emit();
    }

    /**
     * Display environment selection Ui in side window
     * @param shellSvc Shell service
     */
    public static show(shellSvc: ShellService): void {
        shellSvc.showModalWindow({
            content: {
                component: EnvListComponent,
                listeners: {
                    closeSelect: () => shellSvc.hideModalWindow()
                }
            },
            align: 'left',
            alignOffset: document.getElementsByTagName('edf-side-bar')[0].clientWidth
        });
    }
}
